import { stableHash } from './embedding'
import type { IntentChunk, IntentChunkKind } from './types'

type ChunkRule = {
  kind: IntentChunkKind
  canonicalId: string
  pattern: RegExp
  useMatchOnly?: boolean
  ontologyRefs?: string[]
  cogoJsonPaths?: string[]
}

const CHUNK_RULES: ChunkRule[] = [
  {
    kind: 'target',
    canonicalId: 'ui.target.selected.button',
    pattern: /(버튼|구매\s*버튼|button|cta|link|링크)/i,
    useMatchOnly: true,
    ontologyRefs: ['cogo:Button', 'cogo:Control'],
    cogoJsonPaths: ['/blocks/*/components/*'],
  },
  {
    kind: 'style',
    canonicalId: 'style.change.color.primary',
    pattern: /(파란색|빨간색|빨갛게|푸르게|색상|색|primary|blue|red|background|배경|토큰)/i,
    ontologyRefs: ['cogo:DesignToken', 'cogo:ColorToken'],
    cogoJsonPaths: ['/tokens/color', '/blocks/*/style'],
  },
  {
    kind: 'operation',
    canonicalId: 'document.page.updateMeta',
    pattern:
      /((문서|document|페이지|page).*(제목|타이틀|title|이름|name|설명|description|route|경로|layout|레이아웃|rename))|((제목|타이틀|title|이름|name|설명|description|route|경로|rename).*(문서|document|페이지|page))/i,
    ontologyRefs: ['cogo:Page', 'cogo:DocumentPage'],
    cogoJsonPaths: [
      '/manifest/pages/*/title',
      '/manifest/pages/*/description',
      '/manifest/pages/*/route',
    ],
  },
  {
    kind: 'operation',
    canonicalId: 'studio.publish.project',
    pattern: /(배포|게시|publish|deploy)/i,
    ontologyRefs: ['cogo:Publish', 'cogo:Deployment'],
    cogoJsonPaths: ['/metadata/publish', '/manifest'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.editRevision',
    pattern:
      /((figma|피그마|fig\b|\.fig|source|소스).*(node|노드|텍스트|text|이름|name|위치|geometry|opacity|visible|가시성).*(수정|편집|변경|바꿔|edit|rename|change))|((수정|편집|변경|바꿔|edit|rename|change).*(figma|피그마|fig\b|\.fig|source|소스).*(node|노드|텍스트|text|이름|name|위치|geometry|opacity|visible|가시성))/i,
    ontologyRefs: ['cogo:SourceDocument', 'cogo:SourceRevision'],
    cogoJsonPaths: ['/sourceWorkspace/bundle/revisionLineage'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.exportOriginal',
    pattern:
      /((figma|피그마|fig\b|\.fig|source|소스|원본|가져온).*(내보내기|export|다운로드|download))|((내보내기|export|다운로드|download).*(figma|피그마|fig\b|\.fig|source|소스|원본))/i,
    ontologyRefs: ['cogo:SourceDocument'],
    cogoJsonPaths: ['/sourceWorkspace/bundle/sourceArtifactRef'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.applyMapping',
    pattern:
      /((figma|피그마|source|소스).*(매핑|mapping|화면\s*역할|screen\s*role))|((매핑|mapping|화면\s*역할|screen\s*role).*(figma|피그마|source|소스))/i,
    ontologyRefs: ['cogo:SourceDocument', 'cogo:SurfaceCandidate'],
    cogoJsonPaths: ['/sourceWorkspace/pageDecisions', '/sourceWorkspace/surfaceDecisions'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.decideTokens',
    pattern:
      /((figma|피그마|source|소스).*(토큰|token).*(승인|거절|결정|검토|accept|reject|decide|review))|((토큰|token).*(승인|거절|결정|검토|accept|reject|decide|review).*(figma|피그마|source|소스))/i,
    ontologyRefs: ['cogo:DesignToken'],
    cogoJsonPaths: ['/sourceWorkspace/tokenDecisions'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.visualReview',
    pattern:
      /((figma|피그마|source|소스|preview|프리뷰).*(gemma|시각|visual|이미지).*(검토|판정|review|judge))|((gemma|시각|visual|이미지).*(검토|판정|review|judge).*(figma|피그마|source|소스|preview|프리뷰))/i,
    ontologyRefs: ['cogo:VisualEvidence', 'cogo:SurfaceCandidate'],
    cogoJsonPaths: ['/sourceWorkspace/visualJudgements'],
  },
  {
    kind: 'operation',
    canonicalId: 'figma.source.buildAssembly',
    pattern:
      /((figma|피그마|source|소스).*(assembly|어셈블리|조립|manifest|매니페스트).*(생성|구축|build|create))|((assembly|어셈블리|조립|manifest|매니페스트).*(생성|구축|build|create).*(figma|피그마|source|소스))/i,
    ontologyRefs: ['cogo:ApplicationCompositionPlan'],
    cogoJsonPaths: ['/sourceWorkspace/assemblyManifest'],
  },
  {
    kind: 'actionflow',
    canonicalId: 'actionflow.connect.checkout',
    pattern:
      /((결제|checkout|payment).*(플로우|flow|누르면|연결|bind|actionflow|액션|action))|((플로우|flow|누르면|연결|bind|actionflow|액션|action).*(결제|checkout|payment))/i,
    ontologyRefs: ['cogo:ActionFlow'],
    cogoJsonPaths: ['/flows', '/blocks/*/events'],
  },
  {
    kind: 'copy',
    canonicalId: 'copy.change.text',
    pattern: /(문구|텍스트|라벨|label|copy|text|이름|타이틀|title)/i,
    ontologyRefs: ['cogo:Text', 'cogo:Label'],
    cogoJsonPaths: ['/blocks/*/components/*/props/text'],
  },
  {
    kind: 'data',
    canonicalId: 'data.bind.datasource',
    pattern: /(데이터|datasource|api|supabase|목록|리스트|table|테이블)/i,
    ontologyRefs: ['cogo:Datasource', 'cogo:DataBinding'],
    cogoJsonPaths: ['/data', '/datasources', '/blocks/*/bindings'],
  },
  {
    kind: 'navigation',
    canonicalId: 'navigation.open.page',
    pattern: /(페이지|page|route|navigate|이동|열어|open)/i,
    ontologyRefs: ['cogo:Route', 'cogo:Page'],
    cogoJsonPaths: ['/manifest/pages'],
  },
  {
    kind: 'validation',
    canonicalId: 'validation.run.proof',
    pattern: /(검증|테스트|proof|readback|validation|gate)/i,
    ontologyRefs: ['cogo:Proof', 'cogo:Validation'],
    cogoJsonPaths: ['/metadata/proof', '/metadata/validation'],
  },
  {
    kind: 'locale',
    canonicalId: 'locale.translate.app',
    pattern: /(다국어|한국어|영어|일본어|중국어|번역|translate|localize|locale|i18n)/i,
    ontologyRefs: ['cogo:Locale', 'cogo:I18n'],
    cogoJsonPaths: ['/metadata/i18n', '/manifest/locales'],
  },
]

export function detectLocale(text: string): string {
  if (/[가-힣]/.test(text)) return 'ko'
  if (/[\u3040-\u30ff]/.test(text)) return 'ja'
  if (/[\u4e00-\u9fff]/.test(text)) return 'zh'
  return 'en'
}

export function normalizeChunkText(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/\bplease\b/g, '')
    .replace(/(해주세요|해줘|줘|합니다|하세요|해라)\s*$/g, '')
    .replace(/(으로|로|을|를|은|는|이|가|에|에게|와|과|도|만)\s*$/g, '')
    .trim()
}

function splitSegments(text: string): string[] {
  const jsonStart = text.indexOf('{')
  const jsonEnd = text.lastIndexOf('}')
  if (jsonStart >= 0 && jsonEnd > jsonStart) {
    try {
      JSON.parse(text.slice(jsonStart, jsonEnd + 1))
      return [text.trim()]
    } catch {
      // Fall through to natural-language segmentation.
    }
  }
  return text
    .split(/\s*(?:,|;|\n|그리고|그\s*다음|다음에|\band\b|\bthen\b|고\s+)\s*/i)
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function createChunkId(
  inputHash: string,
  kind: IntentChunkKind,
  canonicalId: string,
  text: string,
): string {
  return `chunk_${stableHash(`${inputHash}:${kind}:${canonicalId}:${text}`)}`
}

function createUnknownChunk(text: string, inputHash: string, locale: string): IntentChunk {
  const normalizedText = normalizeChunkText(text)
  return {
    id: createChunkId(inputHash, 'unknown', 'unknown', normalizedText),
    rawText: text,
    normalizedText,
    locale,
    kind: 'unknown',
    confidence: 0.2,
    source: 'local',
  }
}

export function extractIntentChunks(input: string): IntentChunk[] {
  const raw = input.trim()
  if (!raw) return []

  const locale = detectLocale(raw)
  const inputHash = stableHash(raw)
  const segments = splitSegments(raw)
  const chunks: IntentChunk[] = []
  const seen = new Set<string>()

  const addChunk = (segment: string, rule: ChunkRule, match: RegExpMatchArray) => {
    const rawText = rule.useMatchOnly ? match[0] : segment
    const normalizedText = normalizeChunkText(rawText)
    if (!normalizedText) return
    const key = `${rule.kind}:${rule.canonicalId}:${normalizedText}`
    if (seen.has(key)) return
    seen.add(key)
    chunks.push({
      id: createChunkId(inputHash, rule.kind, rule.canonicalId, normalizedText),
      rawText,
      normalizedText,
      locale,
      kind: rule.kind,
      confidence: 0.56,
      source: 'local',
      canonicalId: rule.canonicalId,
      ontologyRefs: rule.ontologyRefs,
      cogoJsonPaths: rule.cogoJsonPaths,
      evidence: {
        inputHash,
        contextHash: 'draft',
      },
    })
  }

  for (const segment of segments) {
    const matches = CHUNK_RULES.flatMap((rule) => {
      const match = segment.match(rule.pattern)
      return match ? [{ rule, match }] : []
    })
    const hasPageMetadataIntent = matches.some(
      ({ rule }) => rule.canonicalId === 'document.page.updateMeta',
    )
    const hasSourceOperationIntent = matches.some(({ rule }) =>
      rule.canonicalId.startsWith('figma.source.'),
    )
    for (const { rule, match } of matches) {
      if (
        hasPageMetadataIntent &&
        ['copy.change.text', 'navigation.open.page'].includes(rule.canonicalId)
      ) {
        continue
      }
      if (
        hasSourceOperationIntent &&
        ['style.change.color.primary', 'copy.change.text'].includes(rule.canonicalId)
      ) {
        continue
      }
      addChunk(segment, rule, match)
    }
  }

  if (chunks.length === 0) {
    chunks.push(createUnknownChunk(raw, inputHash, locale))
  }

  return chunks
}
